import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  accordionClasses,
  accordionSummaryClasses,
} from '@montugroup/design-system';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { styled } from '@mui/material/styles';
import type { PropsWithChildren } from 'react';

import { PartnerKey } from '@/hooks/portal-partner-brand';
import { outOfBandThemeColors } from '@/theme/colors';

export interface ProductCategoryAccordionProps {
  label: string;
  count: number;
  startCollapsed?: boolean;
  showCountAtZero?: boolean;
  stickyHeader?: boolean;
}

const StyledAccordion = styled(Accordion)(() => ({
  position: 'relative',
  [`&.${accordionClasses.expanded}`]: {
    margin: 0,
  },
  '&:before': {
    opacity: 0,
  },
  [`&:not(.${accordionClasses.expanded}) .MuiAccordion-region`]: {
    display: 'none',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  background: '#F6F4EC', // TODO: Replace this with the proper semantic color, once UX decides what to use
  borderTop: '1px solid',
  borderTopColor: '#e8e4d1', // TODO: Replace this with the proper semantic color, once UX decides what to use
  padding: `0 ${theme.spacing(5)}`,
  minHeight: theme.spacing(12),
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  ['&.sticky']: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  [`&.${accordionSummaryClasses.root}.${accordionSummaryClasses.expanded}`]: {
    minHeight: theme.spacing(12),
  },
  [`& .${accordionSummaryClasses.content}`]: {
    gap: theme.spacing(2),
    margin: theme.spacing(3),
    alignItems: 'center',
  },
  [`& .${accordionSummaryClasses.content}.${accordionSummaryClasses.expanded}`]: {
    margin: theme.spacing(3),
  },
}));

const ProductCategoryCount = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.light,
  color: theme.palette.secondary.dark,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(2),
  width: theme.spacing(6),
  height: theme.spacing(6),
  borderRadius: '50%',
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0),
}));

export function ProductCategoryAccordion(props: PropsWithChildren<ProductCategoryAccordionProps>) {
  // This whole block is a temporary hack to get around some design system inconsistency.
  // Once the uMeds brand is rolled out, destroy all of style switching code.
  const sxAccordionSummary =
    outOfBandThemeColors[PartnerKey.UMEDS]?.accordionHeaderColors || outOfBandThemeColors.montu.accordionHeaderColors;

  const { count, label, showCountAtZero, startCollapsed, children, stickyHeader } = props;
  const shouldShowCount = Boolean(count > 0 || showCountAtZero);
  // Use edge-to-edge when using sticky headers
  const rootClassName = stickyHeader ? 'portal-full-width' : '';
  const headerClassName = stickyHeader ? 'sticky' : '';
  const detailClassName = stickyHeader ? 'portal-margin' : '';
  return (
    <Box className={rootClassName}>
      <StyledAccordion defaultExpanded={!startCollapsed} elevation={0}>
        <StyledAccordionSummary
          expandIcon={<KeyboardArrowDownIcon />}
          sx={sxAccordionSummary}
          className={headerClassName}
        >
          <Typography variant="subtitle1" component="h2">
            {label}
          </Typography>
          {shouldShowCount && (
            <ProductCategoryCount>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {count}
              </Typography>
            </ProductCategoryCount>
          )}
        </StyledAccordionSummary>
        <StyledAccordionDetails className={detailClassName}>{children}</StyledAccordionDetails>
      </StyledAccordion>
    </Box>
  );
}

export default ProductCategoryAccordion;
